import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiPlus,
  FiLayers,
  FiBookOpen,
  FiDollarSign,
  FiUsers,
  FiLink2,
  FiUserCheck,
  FiTrash2,
  FiAlertTriangle,
  FiTag,
  FiCheckCircle,
  FiCircle,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { useStateContext } from "../contexts/ContextProvider";
import { userRequest } from "../utils/requestMethods";
import { formatter, getPriceForTerm } from "../utils/currencyUtils";
import * as api from "../utils/classGroupApi";
import { getArmLabel, getTeacherName, pluralize } from "../utils/classGroupHelpers";

// Subcomponents / Modals
import ManageSharedSubjectsModal from "../components/classGroups/ManageSharedSubjectsModal";
import AssignClassTeacherModal from "../components/classGroups/AssignClassTeacherModal";
import SetDefaultTuitionModal from "../components/classGroups/SetDefaultTuitionModal";
import SetDefaultAddOnsModal from "../components/classGroups/SetDefaultAddOnsModal";
import LinkSubclassesModal from "../components/classGroups/LinkSubclassesModal";
import ResultSummaryModal from "../components/classGroups/ResultSummaryModal";
import { AssignClassModal, ConfirmationModal } from "../components";
import SkeletonTable from "../components/common/SkeletonTable";

const ClassGroupDetail = () => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const classGroupId = params?.classGroupId || location.pathname.split("/")[2];

  const { school } = useStateContext();
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term || "1";
  const isGroupingEnabled = school?.settings?.class_grouping_enabled ?? false;
  const subclassLabel = getArmLabel(school);
  const armLower = subclassLabel.toLowerCase();
  const armsPlural = pluralize(subclassLabel, 2).toLowerCase();
  const [checklistOpen, setChecklistOpen] = useState(true);

  // Data state
  const [activeTab, setActiveTab] = useState("subclasses"); // 'subclasses' | 'subjects' | 'fees'
  const [classGroup, setClassGroup] = useState(null);
  const [allClassGroups, setAllClassGroups] = useState([]);
  const [memberClasses, setMemberClasses] = useState([]);
  const [allSchoolClasses, setAllSchoolClasses] = useState([]);
  const [sharedSubjectDocs, setSharedSubjectDocs] = useState([]);
  const [sharedSubjects, setSharedSubjects] = useState([]);
  const [feeState, setFeeState] = useState([]);

  // Stable reference so LinkSubclassesModal's selection state doesn't get reset by
  // an unrelated parent re-render recomputing a fresh array on every render.
  const linkModalAvailableClasses = useMemo(
    () => allSchoolClasses.filter((c) => api.getClassGroupId(c.class_group_id) !== classGroupId),
    [allSchoolClasses, classGroupId],
  );
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [isLinkSubclassesModalOpen, setIsLinkSubclassesModalOpen] = useState(false);
  const [isManageSharedSubjectsModalOpen, setIsManageSharedSubjectsModalOpen] = useState(false);
  const [isAssignTeacherModalOpen, setIsAssignTeacherModalOpen] = useState(false);
  const [selectedSubjectForTeacher, setSelectedSubjectForTeacher] = useState(null);
  const [isSetTuitionModalOpen, setIsSetTuitionModalOpen] = useState(false);
  const [isSetAddOnsModalOpen, setIsSetAddOnsModalOpen] = useState(false);

  // Unlink confirmation
  const [unlinkingClass, setUnlinkingClass] = useState(null);
  const [classTeacherFor, setClassTeacherFor] = useState(null); // arm being given a class teacher
  const [unlinkingLoading, setUnlinkingLoading] = useState(false);

  // Delete class group confirmation
  const [deletingGroup, setDeletingGroup] = useState(false);
  const [deletingGroupLoading, setDeletingGroupLoading] = useState(false);

  // Delete shared subject confirmation
  const [deletingSubject, setDeletingSubject] = useState(null);
  const [deletingSubjectLoading, setDeletingSubjectLoading] = useState(false);

  // Result summary feedback modal
  const [summaryModalConfig, setSummaryModalConfig] = useState({
    isOpen: false,
    title: "",
    subtitle: "",
    type: "info",
    items: [],
    note: "",
  });

  // Fetch all class group details in parallel to eliminate network waterfalls
  const fetchGroupDetails = useCallback(async () => {
    if (!schoolId || !classGroupId) return;

    try {
      setLoading(true);

      const [
        groupsResult,
        membersResult,
        subjectsResult,
        classesResult,
        feesResult,
        teachersResult,
      ] = await Promise.allSettled([
        api.listClassGroups(schoolId),
        api.listClassGroupMembers(schoolId, classGroupId),
        api.listClassGroupSubjects(schoolId, classGroupId),
        school?.code
          ? userRequest.get(`classes?code=${school.code}`)
          : Promise.resolve({ data: { classes: [] } }),
        api.listClassGroupFeeState(schoolId, classGroupId),
        userRequest.get(`/teachers?school_id=${schoolId}&limit=200`),
      ]);

      // 1. Process class groups
      if (groupsResult.status === "fulfilled") {
        const groupList = Array.isArray(groupsResult.value) ? groupsResult.value : [];
        setAllClassGroups(groupList);
        const current = groupList.find((g) => g._id === classGroupId);
        setClassGroup(current || null);
      } else if (groupsResult.reason?.response?.status === 403) {
        toast.error("Class grouping is not enabled for this school");
      }

      // 2. Process member classes
      if (membersResult.status === "fulfilled") {
        const members = Array.isArray(membersResult.value) ? membersResult.value : [];
        setMemberClasses(members);
      } else {
        console.warn("Class group members fetch note:", membersResult.reason);
        setMemberClasses([]);
      }

      // 3. Process shared subjects
      if (subjectsResult.status === "fulfilled") {
        const subjects = Array.isArray(subjectsResult.value) ? subjectsResult.value : [];
        setSharedSubjectDocs(subjects);
      } else {
        console.warn("Shared subjects fetch note:", subjectsResult.reason);
        setSharedSubjectDocs([]);
      }

      // 4. Process all school classes (for subclass linking modal pool)
      if (classesResult.status === "fulfilled") {
        setAllSchoolClasses(classesResult.value?.data?.classes || []);
      } else {
        setAllSchoolClasses([]);
      }

      // 5. Process fee state
      if (feesResult.status === "fulfilled") {
        const fees = Array.isArray(feesResult.value) ? feesResult.value : [];
        setFeeState(fees);
      } else {
        console.warn("Fee state fetch note:", feesResult.reason);
        setFeeState([]);
      }

      // 6. Process teachers
      if (teachersResult.status === "fulfilled") {
        setTeachers(teachersResult.value?.data?.teachers || []);
      } else {
        console.warn("Teachers fetch note:", teachersResult.reason);
        setTeachers([]);
      }
    } catch (err) {
      console.error("Error loading class group details:", err);
      if (err?.response?.status === 403) {
        toast.error("Class grouping is not enabled for this school");
      } else {
        toast.error(api.extractApiErrorMessage(err, "Failed to load class group"));
      }
    } finally {
      setLoading(false);
    }
  }, [schoolId, classGroupId, school?.code]);

  useEffect(() => {
    fetchGroupDetails();
  }, [fetchGroupDetails]);

  // Build the Subjects tab rows from the shared subject documents (the real source of truth
  // for a subject's class-level teacher/assistants), joined with memberClasses only to find
  // which arms are actually linked to each one — linked_classes legitimately varies per arm,
  // but the teacher/assistant data must not, since it represents ONE shared subject.
  useEffect(() => {
    const linkedClassesBySubjectId = new Map();
    memberClasses.forEach((cls) => {
      (cls.subjects || []).forEach((s) => {
        if (!s.subject_ref_id) return;
        const key = String(s.subject_ref_id);
        if (!linkedClassesBySubjectId.has(key)) {
          linkedClassesBySubjectId.set(key, []);
        }
        linkedClassesBySubjectId.get(key).push({
          class_id: cls._id,
          class_name: cls.class_name,
          group_label: cls.group_label,
          teacher: api.getPrimarySubjectTeacher(s),
        });
      });
    });

    setSharedSubjects(
      sharedSubjectDocs.map((doc) => ({
        _id: doc._id,
        subject_name: doc.subject_name,
        subject_code: doc.subject_code,
        subject_teacher: api.getPrimarySubjectTeacher(doc),
        subject_teachers: doc.subject_teachers,
        linked_classes: linkedClassesBySubjectId.get(String(doc._id)) || [],
      })),
    );
  }, [sharedSubjectDocs, memberClasses]);

  // Handle Unlink
  const handleConfirmUnlink = async () => {
    if (!unlinkingClass) return;

    try {
      setUnlinkingLoading(true);
      await api.unlinkSubclassFromClassGroup(schoolId, unlinkingClass._id);
      toast.success(`${unlinkingClass.class_name} removed from its class group`);
      setUnlinkingClass(null);
      fetchGroupDetails();
    } catch (err) {
      console.error("Error unlinking subclass:", err);
      toast.error(api.extractApiErrorMessage(err, "Failed to remove the class from its group"));
    } finally {
      setUnlinkingLoading(false);
    }
  };

  // Handle Delete Class Group — navigates away instead of refetching, since the group no
  // longer exists once this succeeds.
  const handleConfirmDeleteGroup = async () => {
    try {
      setDeletingGroupLoading(true);
      const res = await api.deleteClassGroup(schoolId, classGroupId);
      const freed = res.unlinked_subclasses?.length || 0;
      toast.success(
        `Class group deleted. ${freed} ${pluralize(subclassLabel, freed).toLowerCase()} ${freed === 1 ? "is" : "are"} now standalone classes.`,
      );
      history.push("/classes");
    } catch (err) {
      console.error("Error deleting class group:", err);
      toast.error(api.extractApiErrorMessage(err, "Failed to delete class group"));
    } finally {
      setDeletingGroupLoading(false);
      setDeletingGroup(false);
    }
  };

  // Handle Delete Shared Subject
  const handleConfirmDeleteSubject = async () => {
    if (!deletingSubject) return;

    try {
      setDeletingSubjectLoading(true);
      const res = await api.deleteSharedSubject(schoolId, classGroupId, deletingSubject._id);
      const affected = res.unlinked_embedded_subject_ids?.length || 0;
      toast.success(
        `"${deletingSubject.subject_name}" is no longer shared. It stays in ${affected} ${pluralize(subclassLabel, affected).toLowerCase()} as a separate subject.`,
      );
      setDeletingSubject(null);
      fetchGroupDetails();
    } catch (err) {
      console.error("Error deleting shared subject:", err);
      toast.error(api.extractApiErrorMessage(err, "Failed to delete shared subject"));
    } finally {
      setDeletingSubjectLoading(false);
    }
  };

  // Callback on Default Tuition set
  const handleTuitionSet = (res) => {
    fetchGroupDetails();
    const updatedCount = res.updated_subclasses?.length || 0;
    const conflictCount = res.conflicts?.length || 0;

    setSummaryModalConfig({
      isOpen: true,
      title: "Default fees saved",
      subtitle:
        conflictCount > 0
          ? `Some ${armsPlural} kept their own price`
          : `Applied to every ${armLower}`,
      type: conflictCount > 0 ? "warning" : "success",
      items: [
        {
          label: `${pluralize(subclassLabel, 2)} now on the group's default fees`,
          count: updatedCount,
          badgeType: "success",
          details: memberClasses
            .filter((c) => (res.updated_subclasses || []).includes(c._id))
            .map((c) => `${c.class_name}`),
        },
        ...(conflictCount > 0
          ? [
              {
                label: `${pluralize(subclassLabel, 2)} that kept their own price`,
                count: conflictCount,
                badgeType: "warning",
                details: memberClasses
                  .filter((c) => (res.conflicts || []).includes(c._id))
                  .map((c) => `${c.class_name}`),
              },
            ]
          : []),
      ],
      note:
        conflictCount > 0
          ? `These ${armsPlural} already had a different price, so we left them alone. To change one, open it and edit its fees.`
          : undefined,
    });
  };

  // Callback on Default AddOns set
  const handleAddOnsSet = (res) => {
    fetchGroupDetails();

    // added_to / skipped_existing are keyed by class id -> array of add-on names,
    // unlike tuition/subject endpoints which return flat arrays of class ids.
    const addedTo = res.added_to || {};
    const skippedExisting = res.skipped_existing || {};

    const classIdsWithAdditions = Object.keys(addedTo).filter((id) => addedTo[id]?.length > 0);
    const classIdsWithSkips = Object.keys(skippedExisting).filter(
      (id) => skippedExisting[id]?.length > 0,
    );

    const classNameFor = (classId) =>
      memberClasses.find((c) => c._id === classId)?.class_name || classId;

    setSummaryModalConfig({
      isOpen: true,
      title: "Default extra fees saved",
      subtitle:
        classIdsWithSkips.length > 0
          ? `Some ${armsPlural} already had these fees`
          : `Added to every ${armLower}`,
      type: classIdsWithSkips.length > 0 ? "warning" : "success",
      items: [
        {
          label: `${pluralize(subclassLabel, 2)} that got new extra fees`,
          count: classIdsWithAdditions.length,
          badgeType: "success",
          details: classIdsWithAdditions.map(
            (id) => `${classNameFor(id)}: ${addedTo[id].join(", ")}`,
          ),
        },
        ...(classIdsWithSkips.length > 0
          ? [
              {
                label: `${pluralize(subclassLabel, 2)} that already had a fee with this name`,
                count: classIdsWithSkips.length,
                badgeType: "warning",
                details: classIdsWithSkips.map(
                  (id) => `${classNameFor(id)}: ${skippedExisting[id].join(", ")}`,
                ),
              },
            ]
          : []),
      ],
      note:
        classIdsWithSkips.length > 0
          ? "Those existing fees were left exactly as they were."
          : undefined,
    });
  };

  const getTeacherDisplayName = (teacherId) => getTeacherName(teachers, teacherId);

  // Setup checklist — tells the admin exactly what's left to do for this group, instead
  // of them having to work it out across three tabs.
  const subjectsWithTeacher = sharedSubjects.filter((s) => api.getPrimarySubjectTeacher(s)).length;
  const armsWithClassTeacher = memberClasses.filter((c) => api.getPrimaryClassTeacher(c)).length;
  const armsWithNextClass = memberClasses.filter((c) => c.next_class || c.is_last_class).length;
  const checklist = [
    {
      key: "arms",
      label: `Choose the ${armsPlural} in this group`,
      detail: `${memberClasses.length} added`,
      done: memberClasses.length > 0,
      action: () => setIsLinkSubclassesModalOpen(true),
      actionLabel: `Add ${armsPlural}`,
    },
    {
      key: "subjects",
      label: "Choose the subjects they share",
      detail: `${sharedSubjects.length} shared`,
      done: sharedSubjects.length > 0,
      action: () => setIsManageSharedSubjectsModalOpen(true),
      actionLabel: "Choose subjects",
    },
    {
      key: "subject-teachers",
      label: "Give each shared subject a teacher",
      detail: `${subjectsWithTeacher} of ${sharedSubjects.length}`,
      done: sharedSubjects.length > 0 && subjectsWithTeacher === sharedSubjects.length,
      action: () => setActiveTab("subjects"),
      actionLabel: "Go to subjects",
    },
    {
      key: "class-teachers",
      label: `Give each ${armLower} a class teacher`,
      detail: `${armsWithClassTeacher} of ${memberClasses.length}`,
      done: memberClasses.length > 0 && armsWithClassTeacher === memberClasses.length,
      action: () => setActiveTab("subclasses"),
      actionLabel: `Go to ${armsPlural}`,
    },
    {
      key: "fees",
      label: "Set the default fees",
      detail: classGroup?.default_class_price ? "Set" : "Not set",
      done: Boolean(classGroup?.default_class_price),
      action: () => setIsSetTuitionModalOpen(true),
      actionLabel: "Set fees",
    },
    {
      key: "next-class",
      label: "Set where students go next (for promotion)",
      detail: `${armsWithNextClass} of ${memberClasses.length}`,
      done: memberClasses.length > 0 && armsWithNextClass === memberClasses.length,
      action: () => setActiveTab("subclasses"),
      actionLabel: `Go to ${armsPlural}`,
    },
  ];
  const checklistDone = checklist.filter((item) => item.done).length;
  const nextChecklistItem = checklist.find((item) => !item.done);
  const getClassName = (classId) =>
    allSchoolClasses.find((c) => String(c._id) === String(classId))?.class_name;

  if (!isGroupingEnabled) {
    return (
      <div className="m-2 md:m-10 mt-24 p-6 md:p-10 bg-white rounded-3xl min-h-[60vh] flex flex-col items-center justify-center text-center">
        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 max-w-md">
          <FiAlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <h3 className="text-base font-semibold text-slate-900">Class grouping is turned off</h3>
          <p className="text-xs text-slate-600 mt-1">
            Turn it on in Settings → General to group {armsPlural} and set subjects, teachers and
            tests once for every {armLower}.
          </p>
          <button
            onClick={() => history.push("/settings")}
            className="mt-4 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-8 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      {/* Top Breadcrumbs & Title */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-200">
        <div className="space-y-1">
          <button
            onClick={() => history.push("/classes")}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-indigo-600 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Classes
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
              <FiLayers className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900">
                {classGroup?.name || "Class Group"}
              </h1>
              <p className="text-xs text-slate-500">
                Class group • {memberClasses.length}{" "}
                {pluralize(subclassLabel, memberClasses.length).toLowerCase()}
              </p>
            </div>
          </div>
        </div>

        {/* Action Button Strip */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setIsLinkSubclassesModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-full text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-colors"
          >
            <FiPlus className="w-3.5 h-3.5" />
            Add {armsPlural}
          </button>
          <button
            type="button"
            onClick={() => setIsManageSharedSubjectsModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
          >
            <FiBookOpen className="w-3.5 h-3.5" />
            Choose shared subjects
          </button>
          <button
            type="button"
            onClick={() => setDeletingGroup(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium rounded-full text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors"
          >
            <FiTrash2 className="w-3.5 h-3.5" />
            Delete Class Group
          </button>
        </div>
      </div>

      {/* Setup checklist */}
      {!loading && (
        <div
          className={`mt-6 rounded-2xl border p-4 ${
            nextChecklistItem ? "border-indigo-100 bg-indigo-50/50" : "border-emerald-200 bg-emerald-50/60"
          }`}
        >
          <button
            type="button"
            onClick={() => setChecklistOpen((open) => !open)}
            className="w-full flex items-center justify-between gap-3 text-left"
          >
            <div>
              <p className="text-sm font-semibold text-slate-900">
                {nextChecklistItem
                  ? `Setup: ${checklistDone} of ${checklist.length} done`
                  : "Setup complete"}
              </p>
              <p className="text-xs text-slate-600">
                {nextChecklistItem
                  ? `Next: ${nextChecklistItem.label.toLowerCase()}`
                  : `Every ${armLower} in this group is ready to use.`}
              </p>
            </div>
            {checklistOpen ? (
              <FiChevronUp className="w-4 h-4 text-slate-500 flex-shrink-0" />
            ) : (
              <FiChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
            )}
          </button>

          {checklistOpen && (
            <ul className="mt-3 space-y-1.5">
              {checklist.map((item) => (
                <li
                  key={item.key}
                  className="flex items-center justify-between gap-3 rounded-xl bg-white/80 border border-white px-3 py-2"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    {item.done ? (
                      <FiCheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <FiCircle className="w-4 h-4 text-slate-300 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm truncate ${item.done ? "text-slate-500" : "text-slate-800 font-medium"}`}
                    >
                      {item.label}
                    </span>
                    <span className="text-xs text-slate-400 flex-shrink-0">{item.detail}</span>
                  </div>
                  {!item.done && (
                    <button
                      type="button"
                      onClick={item.action}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex-shrink-0"
                    >
                      {item.actionLabel} →
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-slate-200 my-6">
        <button
          onClick={() => setActiveTab("subclasses")}
          className={`pb-3 px-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "subclasses"
              ? "border-indigo-600 text-indigo-600 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FiUsers className="w-4 h-4" />
          {pluralize(subclassLabel, 2)} ({memberClasses.length})
        </button>
        <button
          onClick={() => setActiveTab("subjects")}
          className={`pb-3 px-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "subjects"
              ? "border-indigo-600 text-indigo-600 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FiBookOpen className="w-4 h-4" />
          Shared Subjects ({sharedSubjects.length})
        </button>
        <button
          onClick={() => setActiveTab("fees")}
          className={`pb-3 px-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === "fees"
              ? "border-indigo-600 text-indigo-600 font-semibold"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <FiDollarSign className="w-4 h-4" />
          Fees
        </button>
      </div>

      {/* Loading state */}
      {loading ? (
        <SkeletonTable rows={4} columns={4} />
      ) : (
        <>
          {/* TAB 1: Member Subclasses */}
          {activeTab === "subclasses" && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {pluralize(subclassLabel, 2)} in this group
                  </h3>
                  <p className="text-xs text-slate-500">
                    Each {armLower} keeps its own students and class teacher.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setIsLinkSubclassesModalOpen(true)}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                  >
                    <FiPlus className="w-3.5 h-3.5" />
                    Add {armLower}
                  </button>
                </div>
              </div>

              {memberClasses.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl p-6">
                  <FiUsers className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">No {armsPlural} yet</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                    Add the classes that belong to {classGroup?.name || "this level"} (e.g.{" "}
                    {classGroup?.name} A, {classGroup?.name} B). Their students and fees stay the
                    same.
                  </p>
                  <button
                    onClick={() => setIsLinkSubclassesModalOpen(true)}
                    className="mt-4 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm"
                  >
                    Add {armsPlural}
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {memberClasses.map((cls) => {
                    const primaryTeacherId = api.getPrimaryClassTeacher(cls);
                    const teacherDisplayName = getTeacherDisplayName(primaryTeacherId);
                    const assistantTeacherIds = api.getActiveAssistantClassTeachers(cls);
                    const nextClassName =
                      cls.next_class?.class_name || getClassName(cls.next_class) || "Already set";

                    return (
                      <div
                        key={cls._id}
                        className="p-5 rounded-2xl border border-slate-200 bg-white hover:border-indigo-200 hover:shadow-sm transition-all flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div>
                              <h4 className="text-base font-bold text-slate-900">
                                {cls.group_label
                                  ? `${subclassLabel} ${cls.group_label}`
                                  : cls.class_name}
                              </h4>
                              {cls.group_label && (
                                <p className="mt-0.5 text-xs text-slate-500">{cls.class_name}</p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => setUnlinkingClass(cls)}
                              title="Remove from this group"
                              className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="space-y-1.5 text-xs text-slate-600 pt-2 border-t border-slate-100">
                            <div className="flex justify-between items-center">
                              <span className="text-slate-500">Class Teacher:</span>
                              {primaryTeacherId ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200">
                                  <FiUserCheck className="w-3 h-3" />
                                  {teacherDisplayName}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setClassTeacherFor(cls)}
                                  className="text-amber-700 font-medium hover:text-amber-800 hover:underline"
                                >
                                  Choose class teacher
                                </button>
                              )}
                            </div>
                            {assistantTeacherIds.length > 0 && (
                              <div className="flex justify-between items-center text-[11px]">
                                <span className="text-slate-500">Assistants:</span>
                                <span className="font-medium text-slate-700">
                                  {assistantTeacherIds.length} assistant
                                  {assistantTeacherIds.length !== 1 ? "s" : ""}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-slate-500">Students:</span>
                              <span className="font-semibold text-slate-800">
                                {cls.studentCount || cls.total_students || 0}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Subjects:</span>
                              <span className="font-semibold text-slate-800">
                                {cls.subjects?.length || cls.subjectCount || 0}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-500">Tuition ({schoolTerm} Term):</span>
                              <span className="font-semibold text-slate-800">
                                {formatter.format(getPriceForTerm(cls.class_price, schoolTerm))}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-slate-500">Next class:</span>
                              {cls.is_last_class ? (
                                <span className="font-semibold text-slate-800">Graduates</span>
                              ) : cls.next_class ? (
                                <span className="font-semibold text-slate-800">{nextClassName}</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => history.push(`/update-class/${cls._id}`)}
                                  className="text-amber-700 font-medium hover:text-amber-800 hover:underline"
                                >
                                  Not set — set it
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                          <button
                            type="button"
                            onClick={() => history.push(`/subjects/${cls._id}`)}
                            className="text-indigo-600 hover:text-indigo-800 font-medium"
                          >
                            Subjects →
                          </button>
                          <button
                            type="button"
                            onClick={() => history.push(`/update-class/${cls._id}`)}
                            className="text-slate-500 hover:text-slate-700"
                          >
                            Edit {armLower}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Shared Subjects */}
          {activeTab === "subjects" && (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">Shared subjects</h3>
                  <p className="text-xs text-slate-500">
                    Each subject here is taught in every {armLower}, with one teacher and one set
                    of tests for the whole group.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsManageSharedSubjectsModalOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors"
                  >
                    <FiLayers className="w-3.5 h-3.5" />
                    Choose shared subjects
                  </button>
                </div>
              </div>

              {sharedSubjects.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl p-6">
                  <FiBookOpen className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-slate-700">No shared subjects yet</p>
                  <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                    Pick the subjects your {armsPlural} already have in common, or add a new one —
                    it will be created in every {armLower}.
                  </p>
                  <div className="mt-4 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setIsManageSharedSubjectsModalOpen(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm"
                    >
                      <FiLayers className="w-3.5 h-3.5" />
                      Choose shared subjects
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-200">
                  <div className="bg-slate-50 px-6 py-3 flex items-center justify-between text-xs font-semibold text-slate-600">
                    <span className="w-1/4">Subject</span>
                    <span className="w-1/5">Subject teacher</span>
                    <span className="w-1/5">{pluralize(subclassLabel, 2)}</span>
                    <span className="w-1/3 text-right">Actions</span>
                  </div>

                  {sharedSubjects.map((subj) => {
                    const primaryTeacher = api.getPrimarySubjectTeacher(subj);
                    const teacherName = getTeacherDisplayName(primaryTeacher);

                    return (
                      <div
                        key={subj._id}
                        className="px-6 py-4 flex items-center justify-between gap-4 bg-white hover:bg-slate-50/60 transition-colors"
                      >
                        <div className="w-1/4 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {subj.subject_name}
                          </p>
                          {subj.subject_code && (
                            <p className="text-xs text-slate-400 font-mono">{subj.subject_code}</p>
                          )}
                        </div>

                        <div className="w-1/5">
                          {primaryTeacher ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200">
                              <FiUserCheck className="w-3.5 h-3.5" />
                              {teacherName}
                            </span>
                          ) : (
                            <span className="text-xs text-amber-700">No teacher yet</span>
                          )}
                          {api.getActiveAssistantTeachers(subj).length > 0 && (
                            <p className="mt-1 text-[11px] text-slate-500">
                              +{api.getActiveAssistantTeachers(subj).length} assistant
                              {api.getActiveAssistantTeachers(subj).length !== 1 ? "s" : ""}
                            </p>
                          )}
                        </div>

                        <div className="w-1/5">
                          <div className="flex flex-wrap gap-1">
                            {subj.linked_classes.map((lc) => {
                              // An arm with its own teacher for this subject isn't changed by
                              // setting the group's subject teacher — show that up front.
                              const ownTeacher =
                                lc.teacher && lc.teacher !== primaryTeacher
                                  ? getTeacherDisplayName(lc.teacher)
                                  : null;
                              return (
                                <span
                                  key={lc.class_id}
                                  className={`px-2 py-0.5 rounded-md border text-[11px] font-medium ${
                                    ownTeacher
                                      ? "bg-amber-50 border-amber-200 text-amber-800"
                                      : "bg-slate-100 border-slate-200 text-slate-700"
                                  }`}
                                  title={
                                    ownTeacher
                                      ? `${lc.class_name} has its own teacher for this subject: ${ownTeacher}`
                                      : lc.class_name
                                  }
                                >
                                  {lc.group_label || lc.class_name}
                                  {ownTeacher && ` · ${ownTeacher}`}
                                </span>
                              );
                            })}
                          </div>
                        </div>

                        <div className="w-1/3 flex items-center justify-end gap-1 flex-wrap">
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedSubjectForTeacher(subj);
                              setIsAssignTeacherModalOpen(true);
                            }}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                          >
                            <FiUserCheck className="w-3.5 h-3.5" />
                            {primaryTeacher ? "Change teacher" : "Set teacher"}
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingSubject(subj)}
                            title="Stop sharing this subject"
                            className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: Fees & Add-ons */}
          {activeTab === "fees" && (
            <div className="space-y-6">
              {/* Top Cards: Defaults */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Default Tuition Card */}
                <div className="p-5 rounded-2xl border border-slate-200 bg-slate-50/70 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiDollarSign className="w-5 h-5 text-indigo-600" />
                      <h4 className="text-sm font-semibold text-slate-900">
                        Default school fees (per term)
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSetTuitionModalOpen(true)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Set default fees
                    </button>
                  </div>

                  {classGroup?.default_class_price ? (
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-white rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold">
                          1st Term
                        </span>
                        <p className="text-xs font-bold text-slate-900 mt-0.5">
                          {formatter.format(classGroup.default_class_price["1"] || 0)}
                        </p>
                      </div>
                      <div className="p-2 bg-white rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold">
                          2nd Term
                        </span>
                        <p className="text-xs font-bold text-slate-900 mt-0.5">
                          {formatter.format(classGroup.default_class_price["2"] || 0)}
                        </p>
                      </div>
                      <div className="p-2 bg-white rounded-xl border border-slate-200">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold">
                          3rd Term
                        </span>
                        <p className="text-xs font-bold text-slate-900 mt-0.5">
                          {formatter.format(classGroup.default_class_price["3"] || 0)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      Not set yet. Setting it here fills in the fees for every {armLower} at once.
                    </p>
                  )}
                </div>

                {/* Default Add-ons Card */}
                <div className="p-5 rounded-2xl border border-slate-200 bg-slate-50/70 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FiTag className="w-5 h-5 text-indigo-600" />
                      <h4 className="text-sm font-semibold text-slate-900">
                        Default extra fees
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsSetAddOnsModalOpen(true)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Set extra fees
                    </button>
                  </div>

                  {classGroup?.default_add_on && classGroup.default_add_on.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {classGroup.default_add_on.map((a, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white border border-slate-200 text-xs text-slate-800"
                        >
                          <span className="font-medium">{a.add_on_name}</span>
                          <span className="font-semibold text-indigo-600">
                            {formatter.format(a.add_on_price || 0)}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 italic">
                      None yet (e.g. uniform, books).
                    </p>
                  )}
                </div>
              </div>

              {/* Subclasses Fee State Matrix */}
              <div className="space-y-3 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">
                      Fees for each {armLower}
                    </h4>
                    <p className="text-xs text-slate-500">
                      "Group default" means the {armLower} uses the fees above. "Custom price" means it was given its own price.
                    </p>
                  </div>
                </div>

                {feeState.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No fee records available.</p>
                ) : (
                  <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-200">
                    <div className="bg-slate-50 px-6 py-3 flex items-center justify-between text-xs font-semibold text-slate-600">
                      <span className="w-1/4">{subclassLabel}</span>
                      <span className="w-1/3">School fees</span>
                      <span className="w-1/3">Extra Fees</span>
                      <span className="w-1/12 text-right">Action</span>
                    </div>

                    {feeState.map((fs) => (
                      <div
                        key={fs.class_id}
                        className="px-6 py-4 flex items-center justify-between gap-4 bg-white hover:bg-slate-50/60"
                      >
                        <div className="w-1/4">
                          <p className="text-sm font-semibold text-slate-900">{fs.class_name}</p>
                        </div>

                        <div className="w-1/3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-800">
                              {formatter.format(getPriceForTerm(fs.class_price, schoolTerm))}
                            </span>
                            {fs.class_price_is_override ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                Custom price
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Group default
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="w-1/3">
                          {fs.add_on && fs.add_on.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {fs.add_on.map((a, aIdx) => (
                                <span
                                  key={aIdx}
                                  className={`px-2 py-0.5 rounded-md text-[10px] font-medium border ${
                                    a.matches_group_default
                                      ? "bg-slate-50 text-slate-700 border-slate-200"
                                      : "bg-amber-50 text-amber-800 border-amber-200"
                                  }`}
                                >
                                  {a.add_on_name}: {formatter.format(a.add_on_price || 0)}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic">No extra fees</span>
                          )}
                        </div>

                        <div className="w-1/12 flex flex-col items-end gap-1 text-right">
                          <button
                            type="button"
                            onClick={() => history.push(`/update-class/${fs.class_id}`)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                          >
                            School fees →
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              history.push(
                                `/addons/${fs.class_id}/${encodeURIComponent(fs.class_name)}`,
                              )
                            }
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                          >
                            Extra fees →
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* MODALS */}

      {/* Link Subclasses Modal */}
      {isLinkSubclassesModalOpen && (
        <LinkSubclassesModal
          isOpen={isLinkSubclassesModalOpen}
          onClose={() => setIsLinkSubclassesModalOpen(false)}
          schoolId={schoolId}
          classGroups={allClassGroups}
          targetClassGroupId={classGroupId}
          lockTargetGroup={true}
          availableClasses={linkModalAvailableClasses}
          subclassLabel={subclassLabel}
          onSuccess={() => fetchGroupDetails()}
        />
      )}

      {/* Manage Shared Subjects Modal */}
      {isManageSharedSubjectsModalOpen && (
        <ManageSharedSubjectsModal
          isOpen={isManageSharedSubjectsModalOpen}
          onClose={() => setIsManageSharedSubjectsModalOpen(false)}
          schoolId={schoolId}
          classGroupId={classGroupId}
          classGroupName={classGroup?.name}
          memberClasses={memberClasses}
          sharedSubjects={sharedSubjects}
          teachers={teachers}
          subclassLabel={subclassLabel}
          onSuccess={() => fetchGroupDetails()}
          onOpenAssignTeacher={(subj) => {
            setSelectedSubjectForTeacher(subj);
            setIsAssignTeacherModalOpen(true);
          }}
        />
      )}

      {/* Choose an arm's class teacher */}
      {classTeacherFor && (
        <AssignClassModal
          school={school}
          teachers={teachers}
          initialClassId={classTeacherFor._id}
          initialClassName={classTeacherFor.class_name}
          lockClass
          initialRole="primary"
          onClose={() => setClassTeacherFor(null)}
          onSuccess={() => {
            toast.success(`Class teacher assigned to ${classTeacherFor.class_name}`);
            fetchGroupDetails();
          }}
        />
      )}

      {/* Assign Shared Subject Teacher Modal */}
      {isAssignTeacherModalOpen && selectedSubjectForTeacher && (
        <AssignClassTeacherModal
          isOpen={isAssignTeacherModalOpen}
          onClose={() => {
            setIsAssignTeacherModalOpen(false);
            setSelectedSubjectForTeacher(null);
          }}
          schoolId={schoolId}
          classGroupId={classGroupId}
          subject={selectedSubjectForTeacher}
          linkedClasses={
            sharedSubjects.find(
              (s) => String(s._id) === String((selectedSubjectForTeacher.subject || selectedSubjectForTeacher)._id),
            )?.linked_classes || []
          }
          subclassLabel={subclassLabel}
          teachersList={teachers}
          onSuccess={() => fetchGroupDetails()}
        />
      )}

      {/* Set Default Tuition Modal */}
      {isSetTuitionModalOpen && (
        <SetDefaultTuitionModal
          isOpen={isSetTuitionModalOpen}
          onClose={() => setIsSetTuitionModalOpen(false)}
          schoolId={schoolId}
          classGroupId={classGroupId}
          classGroupName={classGroup?.name}
          currentDefaultPrice={classGroup?.default_class_price}
          memberClasses={memberClasses}
          subclassLabel={subclassLabel}
          onSuccess={handleTuitionSet}
        />
      )}

      {/* Set Default Add-ons Modal */}
      {isSetAddOnsModalOpen && (
        <SetDefaultAddOnsModal
          isOpen={isSetAddOnsModalOpen}
          onClose={() => setIsSetAddOnsModalOpen(false)}
          schoolId={schoolId}
          classGroupId={classGroupId}
          classGroupName={classGroup?.name}
          currentDefaultAddOns={classGroup?.default_add_on}
          onSuccess={handleAddOnsSet}
        />
      )}

      {/* Unlink Subclass Confirmation Modal */}
      {unlinkingClass && (
        <ConfirmationModal
          isVisible={!!unlinkingClass}
          title="Remove from group"
          description={`Remove "${unlinkingClass.class_name}" from ${classGroup?.name || "this group"}? It becomes a standalone class again. Its students and fees stay the same, but it will no longer share subjects, subject teachers or tests with the other ${armsPlural}.`}
          deleting={unlinkingLoading}
          onConfirm={handleConfirmUnlink}
          onCancel={() => setUnlinkingClass(null)}
        />
      )}

      {/* Delete Class Group Confirmation Modal */}
      {deletingGroup && (
        <ConfirmationModal
          isVisible={deletingGroup}
          title="Delete class group"
          description={`Delete the "${classGroup?.name}" group? Its ${memberClasses.length} ${pluralize(subclassLabel, memberClasses.length).toLowerCase()} and their students are NOT deleted — they become standalone classes again, and their subjects stop being shared. This can't be undone.`}
          deleting={deletingGroupLoading}
          onConfirm={handleConfirmDeleteGroup}
          onCancel={() => setDeletingGroup(false)}
        />
      )}

      {/* Delete Shared Subject Confirmation Modal */}
      {deletingSubject && (
        <ConfirmationModal
          isVisible={!!deletingSubject}
          title="Stop sharing this subject"
          description={`Stop sharing "${deletingSubject.subject_name}"? It stays in each ${armLower} as a normal subject, but the ${armsPlural} will no longer share one teacher or one set of tests for it. You can share it again later.`}
          deleting={deletingSubjectLoading}
          onConfirm={handleConfirmDeleteSubject}
          onCancel={() => setDeletingSubject(null)}
        />
      )}

      {/* Result Summary Modal */}
      {summaryModalConfig.isOpen && (
        <ResultSummaryModal
          isOpen={summaryModalConfig.isOpen}
          onClose={() => setSummaryModalConfig((prev) => ({ ...prev, isOpen: false }))}
          title={summaryModalConfig.title}
          subtitle={summaryModalConfig.subtitle}
          type={summaryModalConfig.type}
          items={summaryModalConfig.items}
          note={summaryModalConfig.note}
          actionButton={summaryModalConfig.actionButton}
        />
      )}
    </div>
  );
};

export default ClassGroupDetail;
